import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, resolveImg, errMsg } from "@/lib/api";
import { Upload, Link2, Trash2, Loader2, ImageIcon } from "lucide-react";

const field =
  "w-full bg-[#0e0e0e] border border-white/12 px-4 py-3 text-white placeholder:text-white/30 text-sm outline-none focus:border-[#e31e24] transition-colors";

export const AdminGallery = () => {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);

  const load = () =>
    api
      .listGallery()
      .then((res) => setItems(res.data))
      .catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setUploading(true);
    try {
      const { data } = await api.uploadGallery(fd);
      setItems((cur) => [data, ...cur]);
      setCaption("");
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addUrl = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    try {
      const { data } = await api.addGalleryUrl(url.trim(), caption);
      setItems((cur) => [data, ...cur]);
      setUrl("");
      setCaption("");
      toast.success("Image added.");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteGallery(id);
      setItems((cur) => cur.filter((i) => i.id !== id));
      toast.success("Removed from gallery.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {/* upload */}
        <div className="bg-[#141414] border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Upload size={16} className="text-[#e31e24]" />
            <span className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/60">
              Upload a photo
            </span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            data-testid="gallery-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-ghost w-full justify-center disabled:opacity-60"
            data-testid="gallery-upload-btn"
          >
            {uploading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ImageIcon size={16} />
            )}
            {uploading ? "Uploading…" : "Choose Image"}
          </button>
          <p className="mt-2 font-mono-rp text-[10px] text-white/40">
            JPG / PNG / WEBP · up to 10MB · works from your phone
          </p>
        </div>

        {/* url */}
        <form onSubmit={addUrl} className="bg-[#141414] border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Link2 size={16} className="text-[#e31e24]" />
            <span className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/60">
              Or paste an image link
            </span>
          </div>
          <input
            className={field}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…/photo.jpg"
            data-testid="gallery-url-input"
          />
          <button
            type="submit"
            className="btn-race w-full justify-center mt-3"
            data-testid="gallery-url-btn"
          >
            Add Image
          </button>
        </form>
      </div>

      <input
        className={field + " mb-8"}
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        placeholder="Optional caption (applied to the next image you add)"
        data-testid="gallery-caption-input"
      />

      {items === null ? (
        <div className="grid place-items-center py-16 text-white/40">
          <Loader2 className="animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="grid place-items-center py-20 text-white/40 border border-white/10 border-dashed">
          <ImageIcon size={28} />
          <p className="mt-3 text-sm">
            No gallery images yet — the site shows placeholders until you add some.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div
              key={it.id}
              className="group relative aspect-square overflow-hidden bg-black border border-white/10"
              data-testid={`gallery-manage-${it.id}`}
            >
              <img
                src={resolveImg(it.image_url)}
                alt={it.caption || "gallery"}
                className="h-full w-full object-cover"
              />
              <button
                onClick={() => remove(it.id)}
                className="absolute top-2 right-2 grid place-items-center h-8 w-8 bg-black/70 text-white hover:bg-[#e31e24] transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100"
                data-testid={`gallery-delete-${it.id}`}
                aria-label="Delete image"
              >
                <Trash2 size={14} />
              </button>
              {it.caption && (
                <span className="absolute bottom-0 inset-x-0 bg-black/70 text-white/80 text-xs px-2 py-1 truncate">
                  {it.caption}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
